<template>
  <div>
    <div class="mt-3 mt-lg-4">
      <validation-observer ref="referral-services-observer">
        <div v-if="isInventoryFlow">
          <referral-service-inventory-item
            v-for="(item, key) in referral.services"
            :key="key + item.record"
            :item="item"
            :inventory-types="inventoryTypes"
            :classifications="classifications"
            :purchase-methods="purchaseMethods"
            :documentation-methods="documentationMethods"
            :is-read-only="isReadOnly"
            :validate="validate"
            @proceed="onProceedChange"
            @required-replacement="onRequiredReplacementChange"
            @inventory-type="onInventoryChange"
            @cancel-documentation="onCancelDocumentation"
            @save-documentation="onSaveDocumentation"
            @perform-service="onPerformService"
            @refresh="getServices"
          ></referral-service-inventory-item>
        </div>
        <div v-else>
          <referral-service-item
            v-for="(item, key) in referral.services"
            :key="key + item.record"
            :item="item"
            :inventory-types="inventoryTypes"
            :classifications="classifications"
            :purchase-methods="purchaseMethods"
            :documentation-methods="documentationMethods"
            :is-read-only="isReadOnly"
            @proceed="onProceedChange"
            @required-replacement="onRequiredReplacementChange"
            @inventory-type="onInventoryChange"
            @cancel-documentation="onCancelDocumentation"
            @save-documentation="onSaveDocumentation"
            @perform-service="onPerformService"
          ></referral-service-item>
        </div>
      </validation-observer>
    </div>
  </div>
</template>

<script>
import Error from 'custom-error';
import CustomDropDown from 'custom-drop-down';
import CustomBalloon from 'custom-balloon';
import DropDown from 'dropdown';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import ServiceDocumentation from './ServiceDocumentation.vue';
import ReferralServiceItem from './ReferralServiceItem.vue';
import ReferralServiceInventoryItem from './ReferralServiceInventoryItem.vue';
import mixAlert from '@/mixins/alert.mixin';

import referralAndEquipmentsService from '@/services/api/referralsandequipments.api';

import PurchaseMethodInventoryTypes from '@/utils/constants/purchaseMethodInventoryTypes';
import PurchaseMethods from '@/utils/constants/purchaseMethods';

export default {
  name: 'ReferralServices',
  components: {
    Error,
    CustomDropDown,
    CustomBalloon,
    DropDown,
    ValidationProvider,
    ValidationObserver,
    ServiceDocumentation,
    ReferralServiceItem,
    ReferralServiceInventoryItem,
  },
  mixins: [mixAlert],
  props: {
    referral: {
      type: Object,
      required: true,
    },
    classifications: {
      type: Array,
      required: true,
    },
    purchaseMethods: {
      type: Array,
      required: true,
    },
    documentationMethods: {
      type: Array,
      required: true,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
    validate: {
      type: Boolean,
      default: false,
    },
    isInventoryFlowEnabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    referralId() {
      return Number(this.$route.params.id);
    },
    isInventoryFlow() {
      return this.isInventoryFlowEnabled && Boolean(this.referral.hasCatalog);
    },
    inventoryTypes() {
      return [
        {
          value: PurchaseMethodInventoryTypes.New,
          text: 'Nuevo',
        },
        {
          value: PurchaseMethodInventoryTypes.Used,
          text: 'Usado',
        },
        {
          value: PurchaseMethodInventoryTypes.Auction,
          text: 'Subasta',
        },
      ];
    },
  },
  async mounted() {
    if (this.referral.services?.length) return;
    await this.getServices();
  },
  methods: {
    async getServices() {
      try {
        const {
          data,
        } = await referralAndEquipmentsService.getServicesByReferralId(
          this.referralId
        );
        this.referral.services = data.map((x, key) => {
          let item = {
            ...x,
            key,
            referral: { ...this.referral },
            originalIsProceed: x.isProceed,
          };
          if (this.isInventoryFlow) {
            item.requestedBySifde =
              x.purchaseMethod?.id === PurchaseMethods.SIFDE;
            const hasInventoryProcess =
              item.requestedBySifde || item.inventoryEquipment;
            if (hasInventoryProcess) {
              item.isProceed = true;
              item.isRequiredReplacement = true;
            }
          }
          return item;
        });
        this.$emit('loaded');
      } catch (error) {
        this.ShowToast(
          'Error',
          'Ha ocurrido un error en la búsqueda de Servicios',
          'error'
        );
      }
    },
    onProceedChange({ value, item }) {
      if (value) return;
      if (item.requestedBySifde || item.inventoryEquipment) {
        return;
      }
      item.isRequiredReplacement = null;
      item.classification = null;
      this.onRequiredReplacementChange({ value: null, item });
    },
    onRequiredReplacementChange({ value, item }) {
      if (value) {
        this.onCancelDocumentation(item);
        return;
      }

      item.purchaseMethod = null;
      item.inventoryType = null;
      this.$refs['referral-services-observer'].reset();
    },
    onInventoryChange({ value, item }) {
      if (!value) return;
      switch (value) {
        case PurchaseMethodInventoryTypes.New:
          item.isNew = true;
          item.isUsed = false;
          item.isAuction = false;
          break;
        case PurchaseMethodInventoryTypes.Used:
          item.isNew = false;
          item.isUsed = true;
          item.isAuction = false;
          break;
        case PurchaseMethodInventoryTypes.Auction:
          item.isNew = false;
          item.isUsed = false;
          item.isAuction = true;
          break;
        default:
          item.isNew = false;
          item.isUsed = false;
          item.isAuction = false;
          break;
      }
    },
    onCancelDocumentation(item) {
      item.record = false;
      this.$forceUpdate();
    },
    async onSaveDocumentation({ item, model }) {
      try {
        const payload = {
          peiAtServiceId: this.referralId,
          referralTypeId: this.referral.referralTypeId,
          recommendationId: item.equipment.id,
          serviceEquiptmentId: item.serviceEquiptmentId,
          ...model,
        };

        const confirmed = await this.ShowCancelModal(
          'Confirmación',
          `¿Está seguro que desea cambiar el estatus del servicio de <b>${item.status.name}</b> a <b>Realizado</b>?`
        );
        if (!confirmed) return;
        await referralAndEquipmentsService.saveServiceDocumentation(payload);
        item.record = false;
        this.$forceUpdate();
        await this.getServices();
        this.$emit('service-saved');
        this.ShowToast(
          'Éxito',
          'Información ha sido guardada exitosamente.',
          'success'
        );
      } catch (error) {
        this.ShowToast('Error', error.response?.data?.message, 'error');
      }
    },
    onPerformService(item) {
      this.referral.services
        .filter((x) => x.id !== item.id)
        .forEach((x) => (x.record = false));
      item.record = true;
      this.$forceUpdate();
    },
  },
};
</script>

<style scoped lang="scss"></style>
